import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { watchlistService } from "@/services/watchlist.service";
import type { WatchlistKind } from "@/types";

export interface SeguirOpcion {
  kind: WatchlistKind;
  valor: string;
  label: string;
}

/**
 * Botón reutilizable para agregar criterios a la watchlist desde cualquier
 * módulo (detalle de licitación, competidores, etc.).
 */
export function SeguirCriterioButton({
  opciones,
  label = "Seguir",
  size = "sm",
  variant = "outline",
}: {
  opciones: SeguirOpcion[];
  label?: string;
  size?: "sm" | "default";
  variant?: "outline" | "ghost" | "default";
}) {
  const queryClient = useQueryClient();
  const disponibles = opciones.filter((o) => o.valor && o.valor.trim().length >= 2);

  const seguir = useMutation({
    mutationFn: (opcion: SeguirOpcion) =>
      watchlistService.add({ kind: opcion.kind, valor: opcion.valor.trim() }),
    onSuccess: (_data, opcion) => {
      toast.success(`Siguiendo «${opcion.valor}» en tu watchlist`);
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: (error: unknown) => {
      const status = (error as { status?: number })?.status;
      toast.error(status === 409 ? "Ya sigues ese criterio" : "No pudimos agregar el criterio");
    },
  });

  if (disponibles.length === 0) return null;

  if (disponibles.length === 1) {
    const unica = disponibles[0];
    return (
      <Button
        variant={variant}
        size={size}
        disabled={seguir.isPending}
        aria-label={`Seguir ${unica.valor}`}
        onClick={() => seguir.mutate(unica)}
      >
        {seguir.isPending ? <Loader2 className="animate-spin" /> : <Eye />}
        {label}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={seguir.isPending} aria-label="Seguir en watchlist">
          {seguir.isPending ? <Loader2 className="animate-spin" /> : <Eye />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Agregar a mi watchlist</DropdownMenuLabel>
        {disponibles.map((o) => (
          <DropdownMenuItem
            key={`${o.kind}-${o.valor}`}
            onSelect={() => seguir.mutate(o)}
            className="flex flex-col items-start gap-0.5"
          >
            <span className="text-xs text-muted-foreground">{o.label}</span>
            <span className="text-sm truncate max-w-full">{o.valor}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
